import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { HERO_SLIDES } from "@/data/content";

const CLIP_MS = 6500;
const EASE = [0.22, 1, 0.36, 1];

/* flattened playlist: each industry plays its clips in turn */
const PLAYLIST = HERO_SLIDES.flatMap((s, si) => s.videos.map((v, ci) => ({ si, ci })));
const SLIDE_START = HERO_SLIDES.map((s, si) => PLAYLIST.findIndex((p) => p.si === si));

const RotatingStatement = ({ slide }) => (
    <div className="mt-6 min-h-[3rem] md:min-h-[4rem]" data-testid="hero-rotating-statement">
        <AnimatePresence mode="wait">
            <motion.div key={slide.key} className="overflow-hidden">
                <motion.p
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "-115%" }}
                    transition={{ duration: 0.8, ease: EASE }}
                    className="font-display text-lg font-light leading-snug text-white/85 md:text-2xl"
                >
                    {slide.statement.split(" ").map((word, i) => (
                        <motion.span
                            key={`${slide.key}-${i}`}
                            className="inline-block"
                            initial={{ opacity: 0, y: 14 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.55, delay: 0.15 + i * 0.05, ease: EASE }}
                        >
                            {word}
                            {"\u00A0"}
                        </motion.span>
                    ))}
                </motion.p>
            </motion.div>
        </AnimatePresence>
    </div>
);

export const Hero = () => {
    const [step, setStep] = useState(0);
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 700], [0, 140]);
    const opacity = useTransform(scrollY, [0, 520], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const px = useSpring(useTransform(mx, [-0.5, 0.5], [14, -14]), { stiffness: 60, damping: 20 });
    const py = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 60, damping: 20 });

    useEffect(() => {
        const t = setInterval(() => setStep((s) => (s + 1) % PLAYLIST.length), CLIP_MS);
        return () => clearInterval(t);
    }, []);

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    const { si: active, ci: clipIdx } = PLAYLIST[step];
    const slide = HERO_SLIDES[active];

    return (
        <section
            className="relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-[#0A0D20]"
            data-testid="home-hero"
            onMouseMove={onMouseMove}
        >
            {HERO_SLIDES.map((s, i) => {
                const isActive = i === active;
                const shouldLoad = isActive || i === (active + 1) % HERO_SLIDES.length;
                const clip = s.videos[isActive ? clipIdx : 0];
                return (
                    <div
                        key={s.key}
                        className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${isActive ? "opacity-100" : "opacity-0"}`}
                    >
                        {shouldLoad ? (
                            <AnimatePresence mode="sync">
                                <motion.video
                                    key={`${s.key}-${isActive ? clipIdx : 0}`}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 1.6, ease: "easeInOut" }}
                                    className={`absolute inset-0 h-full w-full object-cover transition-transform duration-[7000ms] ease-linear ${
                                        isActive ? "scale-105" : "scale-100"
                                    }`}
                                    poster={clip.poster}
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload={isActive ? "auto" : "metadata"}
                                >
                                    <source src={clip.webm} type="video/webm" />
                                    <source src={clip.video} type="video/mp4" />
                                </motion.video>
                            </AnimatePresence>
                        ) : (
                            <img className="h-full w-full object-cover" src={s.poster} alt={s.label} loading="lazy" />
                        )}
                    </div>
                );
            })}

            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D20]/90 via-[#0A0D20]/45 to-[#0A0D20]/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D20] via-transparent to-[#0A0D20]/45" />

            <motion.div
                style={{ y, opacity }}
                className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col px-6 pb-10 pt-28 md:px-12 md:pb-14 md:pt-32 lg:px-20"
            >
                {/* eyebrow — sits just under the navbar */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.15 }}
                    className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.35em] text-white/70"
                >
                    <span className="inline-block h-px w-12 bg-[#F16622]" />
                    MS Group · Est. 2014
                </motion.p>

                {/* headline block — flows under the eyebrow with even spacing */}
                <div className="mt-5 md:mt-6">
                    <motion.div style={{ x: px, y: py }}>
                        <h1
                            className="font-display font-medium leading-[1.04] tracking-tight text-white text-[clamp(2.3rem,min(9.5svh,8.6vw),7rem)]"
                            data-testid="hero-headline"
                        >
                            {["Strong Roots.", "Diverse Businesses.", "One Vision."].map((line, i) => (
                                <span key={line} className="block overflow-hidden pb-1">
                                    <motion.span
                                        className={`block ${i === 2 ? "text-[#F16622]" : ""}`}
                                        initial={{ y: "110%" }}
                                        animate={{ y: 0 }}
                                        transition={{ duration: 1, delay: 0.3 + i * 0.16, ease: EASE }}
                                    >
                                        {line}
                                    </motion.span>
                                </span>
                            ))}
                        </h1>

                        <RotatingStatement slide={slide} />
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <Link
                            to="/group"
                            data-testid="hero-explore-group-btn"
                            className="group inline-flex items-center gap-3 bg-[#F16622] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-white hover:text-[#0A0D20]"
                        >
                            Explore the Group
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/about"
                            data-testid="hero-our-story-btn"
                            className="inline-flex items-center gap-3 border border-white/40 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
                        >
                            Our Story
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                        className="mt-10 flex flex-wrap items-center gap-6"
                    >
                        <div className="flex items-center gap-4" data-testid="hero-industry-indicator">
                            <span className="font-display whitespace-nowrap text-sm tracking-[0.25em] text-[#F16622]">
                                {String(active + 1).padStart(2, "0")} / 0{HERO_SLIDES.length}
                            </span>
                            <span className="h-px w-8 bg-white/30" />
                            <AnimatePresence mode="wait">
                                <motion.span
                                    key={slide.key}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -12 }}
                                    transition={{ duration: 0.45 }}
                                    className="text-[13px] uppercase tracking-[0.3em] text-white/85"
                                >
                                    {slide.label}
                                </motion.span>
                            </AnimatePresence>
                        </div>
                        <div className="flex items-center gap-3">
                            {HERO_SLIDES.map((s, i) => (
                                <button
                                    key={s.key}
                                    onClick={() => setStep(SLIDE_START[i])}
                                    data-testid={`hero-slide-dot-${s.key}`}
                                    aria-label={s.label}
                                    className="group flex h-6 items-center"
                                >
                                    <span
                                        className={`block h-[2px] overflow-hidden transition-all duration-500 ${
                                            i === active ? "w-16 bg-white/25" : "w-8 bg-white/25 group-hover:bg-white/60"
                                        }`}
                                    >
                                        {i === active && <span key={step} className="slide-progress block h-full bg-[#F16622]" />}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </motion.div>
                </div>

                <div className="pointer-events-none absolute right-6 top-[34%] z-[5] hidden -translate-y-1/2 text-right md:right-12 lg:right-20 lg:block">
                    <AnimatePresence mode="wait">
                        <motion.span
                            key={slide.key}
                            initial={{ opacity: 0, y: 60 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -60 }}
                            transition={{ duration: 0.8, ease: EASE }}
                            className="font-display block text-7xl font-semibold leading-[0.95] tracking-tight xl:text-[6.4rem]"
                            style={{ WebkitTextStroke: "1.5px rgba(255,255,255,0.32)", color: "transparent" }}
                            data-testid="hero-industry-word"
                        >
                            {slide.short}
                        </motion.span>
                    </AnimatePresence>
                </div>
            </motion.div>

            <div className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-3 lg:flex">
                <span className="text-[10px] uppercase tracking-[0.35em] text-white/50 [writing-mode:vertical-rl]">Scroll</span>
                <ChevronDown className="h-4 w-4 animate-bounce text-[#F16622]" strokeWidth={1.5} />
            </div>
        </section>
    );
};
